import { ArrowLeft, Info, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  useCitAccrualPreview,
  usePostCitAccrual,
  translateCitAccrualError,
  type CitPreview,
} from '../../../hooks/useCitAccruals'
import { useCitAdjustments } from '../../../hooks/useCitAdjustments'
import { useCurrency } from '../../../hooks/useCurrency'
import { usePermissions } from '../../../hooks/usePermissions'
import { endOfMonthIso } from '../../../lib/citComputation'
import { CitAdjustmentsSection } from './CitAdjustmentsSection'
import { CitConfirmationModal } from './CitConfirmationModal'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const SIZE_LABEL: Record<CitPreview['companySize'], string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
}

interface Props {
  year: number
  month: number
  onBack: () => void
  onPosted: (journalId: string | null) => void
}

export function CitPreviewCard({ year, month, onBack, onPosted }: Props) {
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canPostCit = can('finance.cit_returns')

  const periodEndIso = endOfMonthIso(year, month)
  const adjustments = useCitAdjustments(periodEndIso)

  const adjustmentTotals = useMemo(() => {
    let dis = 0
    let allow = 0
    for (const r of adjustments.rows) {
      if (r.direction === 'disallowable') dis += r.effectiveValue
      else allow += r.effectiveValue
    }
    return {
      totalDisallowables: Math.round(dis * 100) / 100,
      totalAllowables: Math.round(allow * 100) / 100,
    }
  }, [adjustments.rows])

  const { data: preview, isLoading, error } = useCitAccrualPreview(year, month, adjustmentTotals)
  const post = usePostCitAccrual()
  const [showConfirm, setShowConfirm] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`

  async function handleConfirm() {
    if (!preview) return
    if (!canPostCit) {
      toast.error('Not authorized to post CIT accruals')
      return
    }
    setErrorMessage(null)
    try {
      const journalId = await post.mutateAsync({ periodEndIso: preview.periodEndDate })
      toast.success(`CIT accrued for ${monthLabel}.`)
      setShowConfirm(false)
      onPosted(journalId)
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to post CIT accrual'
      const friendly = translateCitAccrualError(raw)
      setErrorMessage(friendly)
      toast.error(friendly)
    }
  }

  if (isLoading || adjustments.isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
      </div>
    )
  }

  if (error || !preview) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-6 text-sm text-red-700">
        Couldn&rsquo;t load CIT preview. {error?.message ?? ''}
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-brand-600"
        >
          <ArrowLeft className="h-4 w-4" />
          Change period
        </button>

        <h2 className="text-base font-semibold text-slate-900">
          CIT for {monthLabel}
        </h2>

        <div className="w-[140px]" />
      </div>

      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6 space-y-4">
        <SectionHeader title="Inputs (year-to-date)" />
        <Row label="Profit Before Tax (YTD)"
             value={format(preview.ytdProfitBeforeTax)}
             muted={preview.ytdProfitBeforeTax <= 0}
             hint={preview.ytdProfitBeforeTax <= 0 ? 'Loss / no profit — adjustments still apply' : undefined} />
        <Row label="Turnover (YTD)"
             value={format(preview.ytdTurnover)} />
        <Row label="Company Size"
             value={SIZE_LABEL[preview.companySize]}
             chip />

        <Divider />

        <CitAdjustmentsSection
          rows={adjustments.rows}
          ytdProfitBeforeTax={preview.ytdProfitBeforeTax}
          totalDisallowables={preview.totalDisallowables}
          totalAllowables={preview.totalAllowables}
          assessableProfit={preview.assessableProfit}
          disabled={post.isPending}
        />

        <Divider />

        <SectionHeader title="Rates" />
        <Row label={`CIT Rate (${SIZE_LABEL[preview.companySize].toLowerCase()})`}
             value={`${preview.citRatePercent.toFixed(2)}%`} />
        <Row label="EDT Rate (TET)"
             value={`${preview.edtRatePercent.toFixed(2)}%`} />
        <Row label="Minimum Tax Rate"
             value={`${preview.minimumTaxRatePercent.toFixed(2)}%`}
             hint={preview.companySize === 'small' ? 'Small companies are exempt' : undefined} />

        <Divider />

        <SectionHeader title="Computation" />
        <Row label={`CIT @ ${preview.citRatePercent.toFixed(2)}% of assessable profit`}
             value={format(preview.citAmount)} />
        <Row label={`EDT @ ${preview.edtRatePercent.toFixed(2)}% of assessable profit`}
             value={format(preview.edtAmount)} />
        <Row label="Minimum Tax @ 0.5% of turnover"
             value={format(preview.minimumTaxAmount)}
             muted={preview.companySize === 'small'} />

        <Divider />

        <Row label="Total Tax Charge YTD"
             value={format(preview.totalTaxCharge)}
             emphasis
             hint="Greater of (CIT + EDT) or Minimum Tax" />
        <Row label="Previously Accrued YTD"
             value={format(preview.priorYtdAccrued)} />

        <Divider thick />

        <Row label="This Period's Charge"
             value={format(preview.periodCharge)}
             emphasis
             highlight
             hint={preview.periodCharge < 0 ? 'True-up down — this releases excess prior accrual' : undefined} />
      </section>

      <HelperCard />

      {canPostCit && (
        <div className="flex justify-end">
          <button
            type="button"
            disabled={post.isPending}
            onClick={() => setShowConfirm(true)}
            className="btn-primary disabled:opacity-50"
          >
            Post Accrual
          </button>
        </div>
      )}

      <CitConfirmationModal
        open={showConfirm}
        monthLabel={monthLabel}
        periodCharge={preview.periodCharge}
        totalTaxCharge={preview.totalTaxCharge}
        posting={post.isPending}
        errorMessage={errorMessage}
        onCancel={() => { setShowConfirm(false); setErrorMessage(null) }}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

function SectionHeader({ title }: { title: string }) {
  return (
    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
      {title}
    </p>
  )
}

function Divider({ thick }: { thick?: boolean }) {
  return (
    <div className={thick ? 'border-t-2 border-slate-200' : 'border-t border-slate-100'} />
  )
}

interface RowProps {
  label: string
  value: string
  muted?: boolean
  emphasis?: boolean
  highlight?: boolean
  chip?: boolean
  hint?: string
}

function Row({ label, value, muted, emphasis, highlight, chip, hint }: RowProps) {
  return (
    <div className={`flex items-start justify-between gap-3 ${muted ? 'opacity-60' : ''}`}>
      <div className="min-w-0">
        <p className={`text-sm ${emphasis ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
          {label}
        </p>
        {hint && (
          <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>
        )}
      </div>
      {chip ? (
        <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
          {value}
        </span>
      ) : (
        <p className={`tabular-nums whitespace-nowrap ${
          highlight
            ? 'text-base font-bold text-brand-700'
            : emphasis
              ? 'text-sm font-semibold text-slate-900'
              : 'text-sm text-slate-800'
        }`}>
          {value}
        </p>
      )}
    </div>
  )
}

function HelperCard() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start gap-3">
        <Info className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-600 leading-relaxed space-y-1">
          <p className="font-semibold text-slate-700">How this is calculated</p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>YTD profit is read from your Income Statement.</li>
            <li>Disallowables auto-compute from your GL; you can override any line.</li>
            <li>Assessable profit = PBT + disallowables − allowables (floor 0).</li>
            <li>CIT and EDT are applied to assessable profit, not raw PBT.</li>
            <li>Posting freezes every adjustment line and creates DR Tax Expense / CR Income Tax Payable.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
